import React from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { useUploadFiles } from "../../../../shared/components/UploadFiles";
import FormLabel from "../../../shared/components/ui/FormLabel";
import InputError from "../../../shared/components/ui/InputError";
import Checkbox from "../../../shared/components/ui/Checkbox";
import { Box } from "../../../shared/components/ui/chakra";
import { Options } from "../../../shared/lib/types";
import {
  ContractorInfoArchitecturalDesignWorksAboveGroundFloorsField,
  ContractorInfoArchitecturalDesignWorksBuildingTypeField,
  ContractorInfoArchitecturalDesignWorksCompletionYearField,
  ContractorInfoArchitecturalDesignWorksImagesField,
  ContractorInfoArchitecturalDesignWorksPrefectureField,
  ContractorInfoArchitecturalDesignWorksProjectDetailField,
  ContractorInfoArchitecturalDesignWorksProjectNameField,
  ContractorInfoArchitecturalDesignWorksTotalFloorAreaField,
  ContractorInfoArchitecturalDesignWorksUndergroundFloorsField,
} from "./FormFields";

import { CheckboxGroup, Stack } from "@chakra-ui/react";

const DesigendArchitecturalFormForNew = ({
  methods: { control },
  namePrefix,
  buildingTypes,
  structureTypes,
  prefectures,
  onChange,
  defaultImages = [],
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  methods: UseFormReturn<any>;
  namePrefix: `contractor_info_attributes.architectural_design_works_attributes.${number}`;
  buildingTypes: Options;
  structureTypes: Options;
  prefectures: Options;
  onChange: (files: File[]) => void;
  defaultImages?: { url: string; signed_id: string }[];
}) => {
  // TODO: 新規登録時にはFile[]しかないが、asは使いたくない
  const [files, addFiles, removeFile] = useUploadFiles(
    defaultImages ?? [],
    [],
    3,
    (files) => onChange(files as File[]),
  );

  return (
    <Stack gap={4}>
      <Controller
        control={control}
        name={`${namePrefix}.project_name`}
        render={({ field, fieldState }) => (
          <ContractorInfoArchitecturalDesignWorksProjectNameField
            field={field}
            fieldState={fieldState}
          />
        )}
      />
      <Controller
        control={control}
        name={`${namePrefix}.building_type_id`}
        render={({ field, fieldState }) => (
          <ContractorInfoArchitecturalDesignWorksBuildingTypeField
            field={field}
            fieldState={fieldState}
            buildingTypes={buildingTypes}
          />
        )}
      />
      <Box as="fieldset">
        <Box as="legend">
          <FormLabel required>構造種別</FormLabel>
        </Box>
        <Box mt={2}>
          <Controller
            control={control}
            name={`${namePrefix}.structure_type_ids`}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Box>
                <CheckboxGroup {...field}>
                  <Stack>
                    {structureTypes.map((structureType) => (
                      <Checkbox
                        key={structureType.id}
                        value={structureType.id.toString()}
                        ref={ref}
                        isInvalid={!!error}
                      >
                        {structureType.name}
                      </Checkbox>
                    ))}
                  </Stack>
                </CheckboxGroup>
                {error?.message && <InputError>{error.message}</InputError>}
              </Box>
            )}
          />
        </Box>
      </Box>
      <Controller
        control={control}
        name={`${namePrefix}.project_detail`}
        render={({ field, fieldState }) => (
          <ContractorInfoArchitecturalDesignWorksProjectDetailField
            field={field}
            fieldState={fieldState}
          />
        )}
      />
      <Controller
        control={control}
        name={`${namePrefix}.prefecture_id`}
        render={({ field, fieldState }) => (
          <ContractorInfoArchitecturalDesignWorksPrefectureField
            field={field}
            fieldState={fieldState}
            prefectures={prefectures}
          />
        )}
      />
      <Controller
        control={control}
        name={`${namePrefix}.above_ground_floors`}
        render={({ field, fieldState }) => (
          <ContractorInfoArchitecturalDesignWorksAboveGroundFloorsField
            field={field}
            fieldState={fieldState}
          />
        )}
      />
      <Controller
        control={control}
        name={`${namePrefix}.underground_floors`}
        render={({ field, fieldState }) => (
          <ContractorInfoArchitecturalDesignWorksUndergroundFloorsField
            field={field}
            fieldState={fieldState}
          />
        )}
      />
      <Controller
        control={control}
        name={`${namePrefix}.total_floor_area`}
        render={({ field, fieldState }) => (
          <ContractorInfoArchitecturalDesignWorksTotalFloorAreaField
            field={field}
            fieldState={fieldState}
          />
        )}
      />
      <Controller
        control={control}
        name={`${namePrefix}.completion_year`}
        render={({ field, fieldState }) => (
          <ContractorInfoArchitecturalDesignWorksCompletionYearField
            field={field}
            fieldState={fieldState}
          />
        )}
      />
      <Box as="fieldset">
        <Box as="legend">
          <FormLabel>建物写真</FormLabel>
        </Box>
        <ContractorInfoArchitecturalDesignWorksImagesField
          files={files}
          addFiles={addFiles}
          removeFile={removeFile}
        />
      </Box>
    </Stack>
  );
};

export default DesigendArchitecturalFormForNew;
